import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ProgressBar } from "@/components/ProgressBar";
import { rupiah } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Target as TargetIcon, TrendingUp, ChevronRight, ChevronDown, Upload, Download, Plus, Eye, EyeOff, FileSpreadsheet, FileText, GitBranch, Trash2 } from "lucide-react";
import { toast } from "sonner";

const THRESHOLD_CLS = {
  gold: "bg-amber-50 text-amber-700 border-amber-300",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
};
const THRESHOLD_LABEL = { tercapai: "Tercapai", hampir: "Hampir", perhatian: "Perlu Perhatian", kritis: "Kritis" };
const UNITS = [["rupiah", "Rupiah (Rp)"], ["unit", "Unit"], ["persentase", "Persentase (%)"], ["angka", "Angka"]];
const PERIODS = [["harian", "Harian"], ["mingguan", "Mingguan"], ["bulanan", "Bulanan"], ["kuartalan", "Kuartalan"], ["tahunan", "Tahunan"]];
const EMPTY_FORM = { name: "", unit: "rupiah", direction: "naik", target_value: "", period: "bulanan", start_date: "", end_date: "", division_id: "", branch_id: "", verifier_user_id: "", parent_target_id: "" };

function fmtVal(t, v) {
  if (t.unit === "rupiah") return rupiah(v);
  if (t.unit === "persentase") return `${v}%`;
  return `${Number(v).toLocaleString("id-ID")} ${t.unit === "unit" ? "unit" : ""}`;
}

function flattenRows(targets, openIds) {
  const byParent = {};
  targets.forEach((t) => {
    const k = t.parent_target_id || "root";
    (byParent[k] = byParent[k] || []).push(t);
  });
  const out = [];
  const push = (key, depth) => {
    (byParent[key] || []).forEach((t) => {
      const children = byParent[t.id] || [];
      out.push({ t, depth, hasChildren: children.length > 0 });
      if (openIds.has(t.id)) push(t.id, depth + 1);
    });
  };
  push("root", 0);
  // include orphans whose parent is hidden
  const seen = new Set(out.map((r) => r.t.id));
  targets.forEach((t) => { if (!seen.has(t.id)) out.push({ t, depth: 0, hasChildren: false }); });
  return out;
}

export default function Targets() {
  const { user } = useAuth();
  const isLeader = ["super_admin", "owner", "kepala_divisi", "atasan"].includes(user?.role);
  const [targets, setTargets] = useState([]);
  const [openIds, setOpenIds] = useState(new Set());
  const initRef = useRef(false);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [actualVal, setActualVal] = useState("");
  const [actualNote, setActualNote] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importResult, setImportResult] = useState(null);
  // create
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [divisions, setDivisions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  // visibility
  const [visOpen, setVisOpen] = useState(false);
  const [visMode, setVisMode] = useState("semua");
  const [visUsers, setVisUsers] = useState([]);

  const load = async () => {
    const r = await api.get("/targets");
    setTargets(r.data);
    if (!initRef.current) {
      initRef.current = true;
      setOpenIds(new Set(r.data.filter((t) => !t.parent_target_id && r.data.some((c) => c.parent_target_id === t.id)).map((t) => t.id)));
    }
  };
  useEffect(() => {
    load();
    if (isLeader) {
      api.get("/divisions").then((r) => setDivisions(r.data));
      api.get("/branches").then((r) => setBranches(r.data));
      api.get("/org/users").then((r) => setUsers(r.data));
    }
  }, []);

  const openDetail = async (t) => { setDetail(t); setDetailData(null); const r = await api.get(`/targets/${t.id}`); setDetailData(r.data); };
  const toggle = (id) => setOpenIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const openCreate = (parentId = "") => { setForm({ ...EMPTY_FORM, parent_target_id: parentId }); setCreateOpen(true); };

  const submitCreate = async () => {
    if (!form.name.trim() || !form.target_value) return toast.error("Nama & nilai target wajib diisi");
    setSaving(true);
    try {
      const payload = { ...form, target_value: Number(form.target_value) };
      ["division_id", "branch_id", "verifier_user_id", "parent_target_id", "start_date", "end_date"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      await api.post("/targets", payload);
      toast.success("Target dibuat");
      setCreateOpen(false); setForm(EMPTY_FORM);
      initRef.current = false; await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat target"); }
    setSaving(false);
  };

  const submitActual = async () => {
    if (!actualVal) return toast.error("Nilai wajib diisi");
    await api.post(`/targets/${detail.id}/actuals`, { value: Number(actualVal), note: actualNote });
    toast.success("Aktual dikirim, menunggu verifikasi"); setActualVal(""); setActualNote("");
    const r = await api.get(`/targets/${detail.id}`); setDetailData(r.data); load();
  };
  const verifyActual = async (aid) => { await api.post(`/targets/actuals/${aid}/verify`); toast.success("Aktual diverifikasi"); const r = await api.get(`/targets/${detail.id}`); setDetailData(r.data); load(); };

  const openVisibility = () => {
    setVisMode(detailData?.visibility === "terbatas" ? "terbatas" : "semua");
    setVisUsers(Array.isArray(detailData?.visible_user_ids) ? detailData.visible_user_ids : []);
    setVisOpen(true);
  };
  const saveVisibility = async () => {
    try {
      await api.put(`/targets/${detail.id}/visibility`, { visibility: visMode, visible_user_ids: visMode === "terbatas" ? visUsers : [] });
      toast.success("Visibilitas target diperbarui");
      setVisOpen(false);
      const r = await api.get(`/targets/${detail.id}`); setDetailData(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const deleteTarget = async () => {
    if (!window.confirm(`Hapus target "${detail.name}" beserta semua sub-targetnya?`)) return;
    try {
      await api.delete(`/targets/${detail.id}`);
      toast.success("Target dihapus");
      setDetail(null); setDetailData(null);
      initRef.current = false; await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const rows = flattenRows(targets, openIds);

  const exportTargets = async (fmt) => {
    try {
      const res = await api.get(`/export/targets.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `target_zoa.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Target diekspor (${fmt.toUpperCase()})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal ekspor"); }
  };

  const downloadTemplate = async () => {
    const res = await api.get("/targets/export/template", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = "template_aktual_target.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader(); reader.onload = (ev) => setCsvText(ev.target.result); reader.readAsText(f);
  };
  const runImport = async () => {
    if (!csvText.trim()) return toast.error("Tempel CSV atau unggah file dulu");
    try {
      const r = await api.post("/targets/import-actuals", { csv_text: csvText });
      setImportResult(r.data);
      toast.success(`${r.data.imported} aktual diimpor (menunggu verifikasi)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal impor"); }
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><TargetIcon className="h-7 w-7 text-indigo-500" />Target</h1>
          <p className="text-slate-500 text-sm">Susun target & sub-target, input aktual, pantau pencapaian & pacing</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isLeader && <Button onClick={() => openCreate("")} data-testid="create-target-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Buat Target</Button>}
          {isLeader && <Button variant="outline" onClick={() => exportTargets("xlsx")} data-testid="export-target-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" />Excel</Button>}
          {isLeader && <Button variant="outline" onClick={() => exportTargets("pdf")} data-testid="export-target-pdf-btn"><FileText className="h-4 w-4 mr-1.5 text-rose-600" />PDF</Button>}
          <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); }} data-testid="import-actuals-btn"><Upload className="h-4 w-4 mr-1.5" />Impor Aktual</Button>
        </div>
      </div>

      <div className="max-w-3xl">
        {rows.map(({ t, depth, hasChildren }) => {
          const th = t.threshold || { color: "red", label: "kritis" };
          const expanded = openIds.has(t.id);
          return (
            <div key={t.id} style={{ marginLeft: depth * 16 }}>
              <div onClick={() => openDetail(t)} data-testid={`target-card-${t.id}`}
                className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-all mb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {hasChildren && (
                      <button onClick={(e) => { e.stopPropagation(); toggle(t.id); }} className="text-slate-400 shrink-0">
                        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate flex items-center gap-1.5">{t.name}
                        {t.visibility === "terbatas" && <EyeOff className="h-3.5 w-3.5 text-amber-500" title="Visibilitas terbatas" />}
                      </p>
                      <p className="text-xs text-slate-400">{t.branch?.name || t.division?.name || "Perusahaan"} · {t.period}</p>
                    </div>
                  </div>
                  <span className={cn("shrink-0 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold", THRESHOLD_CLS[th.color])}>
                    {t.achievement_pct}% · {THRESHOLD_LABEL[th.label] || th.label}
                  </span>
                </div>
                <div className="mt-2"><ProgressBar value={Math.min(100, t.achievement_pct)} /></div>
                <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
                  <span>Aktual: <b className="text-slate-700">{fmtVal(t, t.actual)}</b> / {fmtVal(t, t.target_value)}</span>
                  <div className="flex items-center gap-3">
                    {t.pacing?.projection != null && <span className="inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" />Proyeksi: {fmtVal(t, t.pacing.projection)}</span>}
                    {isLeader && <button onClick={(e) => { e.stopPropagation(); openCreate(t.id); }} className="inline-flex items-center gap-1 text-indigo-600 hover:underline" data-testid={`add-subtarget-${t.id}`}><GitBranch className="h-3 w-3" />Sub-target</button>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <p className="text-slate-400 text-center py-12" data-testid="targets-empty">Belum ada target. {isLeader && "Klik \"Buat Target\" untuk memulai."}</p>}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto zoa-scroll" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detailData && (
            <div className="space-y-4">
              {isLeader && (
                <div className="flex justify-end -mb-2">
                  <Button size="sm" variant="ghost" className="h-7 text-rose-600 hover:bg-rose-50" onClick={deleteTarget} data-testid="delete-target-btn"><Trash2 className="h-4 w-4 mr-1" />Hapus Target</Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-slate-400">Target</p><p className="font-bold">{fmtVal(detailData, detailData.target_value)}</p></div>
                <div><p className="text-xs text-slate-400">Aktual Terverifikasi</p><p className="font-bold">{fmtVal(detailData, detailData.actual)}</p></div>
                <div><p className="text-xs text-slate-400">Pencapaian</p><p className="font-bold">{detailData.achievement_pct}%</p></div>
                <div><p className="text-xs text-slate-400">Verifikator</p><p className="font-medium">{detailData.verifier?.name || "-"}</p></div>
              </div>

              {isLeader && (
                <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                  <span className="flex items-center gap-1.5 text-slate-600">
                    {detailData.visibility === "terbatas" ? <EyeOff className="h-4 w-4 text-amber-500" /> : <Eye className="h-4 w-4 text-emerald-500" />}
                    Visibilitas: <b>{detailData.visibility === "terbatas" ? `Terbatas (${(detailData.visible_user_ids || []).length} user)` : "Semua bisa lihat"}</b>
                  </span>
                  <Button size="sm" variant="outline" className="h-7" onClick={openVisibility} data-testid="edit-visibility-btn">Atur</Button>
                </div>
              )}

              {detailData.pacing?.expected != null && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Pacing</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-xs text-slate-400">Seharusnya</p><p className="font-semibold">{fmtVal(detailData, detailData.pacing.expected)}</p></div>
                    <div><p className="text-xs text-slate-400">Proyeksi</p><p className="font-semibold">{fmtVal(detailData, detailData.pacing.projection)}</p></div>
                    <div><p className="text-xs text-slate-400">Waktu</p><p className="font-semibold">{detailData.pacing.elapsed_pct}%</p></div>
                  </div>
                </div>
              )}
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Input Aktual (Maker–Checker)</p>
                <div className="flex gap-2 mb-2">
                  <Input type="number" placeholder="Nilai aktual" value={actualVal} onChange={(e) => setActualVal(e.target.value)} data-testid="actual-value-input" />
                  <Button size="sm" onClick={submitActual} data-testid="actual-submit-btn">Kirim</Button>
                </div>
                <Input placeholder="Catatan" value={actualNote} onChange={(e) => setActualNote(e.target.value)} className="mb-2" />
                <div className="space-y-1.5 mt-2">
                  {detailData.actuals?.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-sm rounded-lg bg-slate-50 px-2.5 py-1.5">
                      <span>{fmtVal(detailData, a.value)} <span className="text-xs text-slate-400">· {a.submitted_by_name}</span></span>
                      {a.status === "terverifikasi" ? <span className="text-xs text-emerald-600 font-semibold">✓ Terverifikasi</span> :
                        a.status === "ditolak" ? <span className="text-xs text-rose-500">Ditolak</span> :
                        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => verifyActual(a.id)} data-testid={`verify-actual-${a.id}`}>Verifikasi</Button>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / sub-target dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto zoa-scroll" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{form.parent_target_id ? "Buat Sub-target" : "Buat Target Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {form.parent_target_id && <p className="text-xs text-indigo-600 bg-indigo-50 rounded-lg px-3 py-2">Sub-target dari: <b>{targets.find((t) => t.id === form.parent_target_id)?.name}</b></p>}
            <div className="space-y-1.5"><Label>Nama Target</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="target-name-input" placeholder="mis. Omzet Cabang A Oktober" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Satuan</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="target-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Nilai Target</Label><Input type="number" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} data-testid="target-value-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Periode</Label>
                <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                  <SelectTrigger data-testid="target-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Arah</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="naik">Naik (makin tinggi makin baik)</SelectItem><SelectItem value="turun">Turun (makin rendah makin baik)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Mulai</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Berakhir</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Cabang (opsional)</Label>
                <Select value={form.branch_id || "none"} onValueChange={(v) => setForm({ ...form, branch_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="target-branch-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Divisi (opsional)</Label>
                <Select value={form.division_id || "none"} onValueChange={(v) => setForm({ ...form, division_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="target-division-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Verifikator Aktual (opsional)</Label>
              <Select value={form.verifier_user_id || "none"} onValueChange={(v) => setForm({ ...form, verifier_user_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="target-verifier-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!form.parent_target_id && (
              <div className="space-y-1.5">
                <Label>Bagian dari Target Induk (opsional)</Label>
                <Select value={form.parent_target_id || "none"} onValueChange={(v) => setForm({ ...form, parent_target_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="target-parent-select"><SelectValue placeholder="— target utama —" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— target utama —</SelectItem>{targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Batal</Button><Button onClick={submitCreate} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-target-btn">{saving ? "Menyimpan…" : "Simpan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visibility dialog */}
      <Dialog open={visOpen} onOpenChange={setVisOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto zoa-scroll" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Atur Visibilitas Target</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <button onClick={() => setVisMode("semua")} data-testid="vis-mode-semua" className={cn("flex-1 rounded-xl border px-3 py-2 text-sm font-semibold", visMode === "semua" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600")}><Eye className="h-4 w-4 inline mr-1.5" />Semua bisa lihat</button>
              <button onClick={() => setVisMode("terbatas")} data-testid="vis-mode-terbatas" className={cn("flex-1 rounded-xl border px-3 py-2 text-sm font-semibold", visMode === "terbatas" ? "border-amber-400 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600")}><EyeOff className="h-4 w-4 inline mr-1.5" />Terbatas</button>
            </div>
            {visMode === "terbatas" && (
              <div>
                <p className="text-xs text-slate-400 mb-2">Pilih user yang boleh melihat target & realisasi aktualnya (pemilik & admin selalu bisa).</p>
                <div className="grid grid-cols-1 gap-1 max-h-60 overflow-y-auto zoa-scroll rounded-lg border border-slate-200 p-2">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer rounded px-1.5 py-1 hover:bg-slate-50" data-testid={`vis-user-${u.id}`}>
                      <Checkbox checked={visUsers.includes(u.id)} onCheckedChange={() => setVisUsers((p) => p.includes(u.id) ? p.filter((x) => x !== u.id) : [...p, u.id])} />
                      {u.name} <span className="text-xs text-slate-400">· {u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setVisOpen(false)}>Batal</Button><Button onClick={saveVisibility} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-visibility-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Impor Aktual Target Massal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Kolom CSV: <code className="text-xs">target_id,nama_target,unit,periode,nilai_aktual,catatan</code>. Aktual masuk sebagai "menunggu verifikasi".</p>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4 mr-1.5" />Unduh Template</Button>
            <div>
              <Label className="text-xs">Unggah file CSV</Label>
              <Input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">atau tempel isi CSV</Label>
              <Textarea rows={5} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="target_id,nama_target,unit,periode,nilai_aktual,catatan" className="font-mono text-xs mt-1" data-testid="import-csv-textarea" />
            </div>
            {importResult && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="import-result">
                <p className="font-semibold text-emerald-700">{importResult.imported} aktual berhasil diimpor</p>
                {importResult.errors?.length > 0 && <ul className="text-xs text-rose-600 mt-1 list-disc pl-4">{importResult.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setImportOpen(false)}>Tutup</Button><Button onClick={runImport} data-testid="run-import-btn" className="bg-indigo-600 hover:bg-indigo-700">Impor</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
